import { IContentBuilderFieldColor } from '@core/models/content-builder-fields.model';
import { catchError, map, skipWhile, takeUntil, tap } from 'rxjs/operators';
import { Observable, Subject, of } from 'rxjs';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';

import { UserService } from '@core/services/user/user.service';
import { PongoService } from '@core/services/pongo/pongo.service';
import { ModalsService } from '@core/services/modals/modals.service';
import { AnalyticsService } from '@core/services/app/analytics.service';
import { isDoodAnonymousJwt } from '@core/helpers/dood-anonymous-jwt.helper';

import { UserKeys } from '@config/keys/user.keys';
import { PatternUtil } from '@shared/utils/pattern/pattern.utils';
import { ModalStoreSelector } from '@common/selectors/modal.selector';
import { DoodUserModel } from '@store/authentication/authentication.model';
import { SettingsStoreSelector } from '@common/selectors/settings.selector';
import { AuthStoreSelector } from '@common/selectors/authentication.selector';
import { MarketplaceStoreSelector } from '@common/selectors/marketplace.selector';
import { ModalScrollBlockBase } from '@core/base/modalScrollBlock/modal-scroll-block.base';

@Component({
  selector: 'app-pongo-create-or-link-account-modal',
  templateUrl: './pongo-create-or-link-account-modal.component.html',
  standalone: false,
})
export class PongoCreateOrLinkAccountModalComponent
  extends ModalScrollBlockBase
  implements OnInit, OnDestroy
{
  private _destroyerRef = new Subject<boolean>();

  static handle = 'pongo-create-or-link-account-modal';

  @Input() backgroundColor?: IContentBuilderFieldColor;

  @Input() modalHeading = 'pongo.join-modal-heading';

  isDesktop$ = this.settingsSelector.selectDeviceIsDesktopScreen;

  index$: Observable<number> = this.modalSelector
    .selectModal(PongoCreateOrLinkAccountModalComponent.handle)
    .pipe(map(el => (el?.index ? el.index : 4)));

  isLoading = true;
  accountExists = false;
  isValidating = false;
  phoneNumberDefinedByUser = true;
  phoneErrorMessage?: string;
  errorMessage?: string;
  form: UntypedFormGroup;

  constructor(
    private readonly fb: UntypedFormBuilder,
    private authSelector: AuthStoreSelector,
    private readonly userService: UserService,
    private modalSelector: ModalStoreSelector,
    private readonly pongoService: PongoService,
    private readonly modalsService: ModalsService,
    private settingsSelector: SettingsStoreSelector,
    private marketplaceSelector: MarketplaceStoreSelector,
    private readonly analyticsService: AnalyticsService,
  ) {
    super();
    this.form = this.fb.group({
      [UserKeys.Phone]: ['', [Validators.pattern(PatternUtil.phone)]],
      optin: [false],
    });
    this.phoneNumberDefinedByUser = false;

    this.authSelector.selectUser$
      .pipe(
        takeUntil(this._destroyerRef),
        skipWhile(user => !user),
        tap(user => user && this.updateForm(user)),
      )
      .subscribe();
  }

  ngOnInit(): void {
    const user = this.authSelector.selectUser();
    if (user) {
      this.updateForm(user);
    }
    this.requireRealAccountThenContinue();
  }

  /**
   * Pongo attach-customer needs a real Dood account (not a guest JWT).
   * Bounce to login and reopen this modal after a successful auth.
   */
  private requireRealAccountThenContinue(): void {
    if (this.isCb2Preview()) {
      this.analyticsService.trackLoyaltyModalDisplay('pongo', 'create_or_link_account');
      this.checkIfAccountAlreadyExists();
      return;
    }

    if (!this.hasRealAccount()) {
      this.pongoService.rememberCreateOrLinkAfterAuth();
      this.modalsService.close(PongoCreateOrLinkAccountModalComponent.handle);
      this.modalsService.open('authentication-modal');
      return;
    }

    this.analyticsService.trackLoyaltyModalDisplay('pongo', 'create_or_link_account');
    this.checkIfAccountAlreadyExists();
  }

  private isCb2Preview(): boolean {
    return (
      typeof window !== 'undefined' &&
      new URLSearchParams(window.location.search).get('cbPreview') === '1'
    );
  }

  /**
   * Real Dood account = store user that is not anonymous.
   * A Dood guest JWT is not enough even if a user snapshot is present.
   */
  private hasRealAccount(): boolean {
    const user = this.authSelector.selectUser();
    if (!user || user[UserKeys.IsAnonymous]) {
      return false;
    }

    const token = this.authSelector.selectStatusTokenSignal?.();
    return !token || !isDoodAnonymousJwt(token);
  }

  updateForm(user: DoodUserModel): void {
    const phoneNumber = user[UserKeys.Phone];

    let phoneValidators = [Validators.pattern(PatternUtil.phone)];

    if (phoneNumber) {
      phoneValidators.push(Validators.required);
    }

    this.form = this.fb.group({
      [UserKeys.Phone]: ['', phoneValidators],
      optin: ['', []],
    });

    this.form.setValue({
      [UserKeys.Phone]: phoneNumber ?? '',
      optin: false,
    });

    if (this.form.invalid || !phoneNumber) {
      this.phoneNumberDefinedByUser = false;
    }
  }

  ngOnDestroy(): void {
    this._destroyerRef.next(true);
    this._destroyerRef.complete();
    super.ngOnDestroy();
  }

  close(): void {
    this.analyticsService.trackLoyaltyButtonClick('pongo', 'continue_without_loyalty');
    this.modalsService.close(PongoCreateOrLinkAccountModalComponent.handle);
  }

  private checkIfAccountAlreadyExists(): void {
    // Read the current store snapshot — combineLatest(selectUser$) can hang
    // because toObservable does not always emit to a late subscriber.
    const user = this.authSelector.selectUser();
    const accountExists = this.pongoService.hasPongoCustomer(user);

    if (!accountExists) {
      this.isLoading = false;
      this.accountExists = false;
      return;
    }
    this.checkIfAccountStillExistsOnPongo();
  }

  private checkIfAccountStillExistsOnPongo(): void {
    this.pongoService
      .getCustomer$()
      .pipe(
        takeUntil(this._destroyerRef),
        tap(() => {
          this.isLoading = false;
          this.accountExists = true;
        }),
        catchError(() => {
          this.isLoading = false;
          this.accountExists = false;
          return of(undefined);
        }),
      )
      .subscribe();
  }

  attachCustomer(): void {
    if (this.form.invalid || this.isValidating) {
      return;
    }
    this.analyticsService.trackLoyaltyButtonClick('pongo', 'link_account');
    this.isValidating = true;
    this.phoneErrorMessage = undefined;
    this.errorMessage = undefined;

    this.pongoService
      .attachCustomer$(this.form.value.phone)
      .pipe(
        tap(user => this.userService.setUser(user)),
        tap(() => this.close()),
      )
      .subscribe({
        error: error => this.handleAttachErrorResponse(error.error),
        complete: () => (this.isValidating = false),
      });
  }

  private handleAttachErrorResponse(error: any): void {
    this.isValidating = false;
    switch (error.code) {
      case 202:
        this.createCustomer();
        break;
      default:
        this.errorMessage = 'pongo.errors.generic-error';
    }
  }

  createCustomer(): void {
    if (this.form.invalid || this.isValidating) {
      return;
    }
    this.isValidating = true;
    this.errorMessage = undefined;
    this.phoneErrorMessage = undefined;

    let phoneNumber = this.form.value[UserKeys.Phone];
    let hasOptin = this.form.value['optin'];
    this.pongoService
      .createCustomer$(phoneNumber, hasOptin)
      .pipe(
        tap(user => this.userService.setUser(user)),
        tap(() => this.close()),
      )
      .subscribe({
        error: error => this.handleErrorResponse(error.error),
        complete: () => (this.isValidating = false),
      });
  }

  private handleErrorResponse(error: any): void {
    this.isValidating = false;
    switch (error.code) {
      case 103:
        this.phoneErrorMessage = 'pongo.create-account.errors.account-already-exists';
        this.analyticsService.trackErrorDisplay(
          'account_exists',
          error.code?.toString(),
          this.phoneErrorMessage,
          'pongo_create_account',
        );
        break;
      case 100:
        this.phoneErrorMessage = 'signup.invalid-phone';
        this.analyticsService.trackErrorDisplay(
          'phone_format',
          error.code?.toString(),
          this.phoneErrorMessage,
          'pongo_create_account',
        );
        break;
      default:
        this.errorMessage = 'pongo.errors.generic-error';
        this.analyticsService.trackErrorDisplay(
          'loyalty_error',
          error.code?.toString(),
          this.errorMessage,
          'pongo_create_account',
        );
    }
  }
}
